/**
 * Home-screen install banner.
 *
 * The banner is suppressed permanently once the app is installed (display-mode
 * standalone, iOS navigator.standalone, or the `appinstalled` event) and until
 * the next session-independent reset once dismissed. Settings can re-open it
 * manually via `openInstallPrompt()`.
 */

import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const DISMISS_KEY = "landoo.installPrompt.dismissed";
const INSTALLED_KEY = "landoo.installPrompt.installed";
const OPEN_EVENT = "landoo:open-install-prompt";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function read(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function persist(key: string) {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* storage unavailable — suppression falls back to in-memory state */
  }
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return true;
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return Boolean(standalone || iosStandalone);
}

/** True when the app is installed — used to hide install affordances. */
export function isInstalled(): boolean {
  if (typeof window === "undefined") return true;
  return isStandalone() || read(INSTALLED_KEY);
}

/** Lets Settings re-open the banner after the user dismissed it. */
export function openInstallPrompt() {
  try {
    window.localStorage.removeItem(DISMISS_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function InstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHint, setIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // iOS/iPadOS never fires beforeinstallprompt — the Share-sheet hint is the
    // only install affordance there.
    const ua = window.navigator.userAgent;
    const ios = /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua);
    setIosHint(ios);

    const auto = !isInstalled() && !read(DISMISS_KEY);
    if (auto && ios) setVisible(true);

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
      if (!isInstalled() && !read(DISMISS_KEY)) setVisible(true);
    };
    const onInstalled = () => {
      persist(INSTALLED_KEY);
      setVisible(false);
    };
    const onManualOpen = () => {
      if (!isInstalled()) setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener(OPEN_EVENT, onManualOpen);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener(OPEN_EVENT, onManualOpen);
    };
  }, []);

  const close = () => {
    setVisible(false);
    persist(DISMISS_KEY);
  };

  const install = async () => {
    if (!event) return;
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") persist(INSTALLED_KEY);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="pointer-events-auto fixed inset-x-3 bottom-[86px] z-40 mx-auto max-w-md rounded-2xl border border-border bg-background/95 p-3 shadow-float backdrop-blur md:bottom-4"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
      role="dialog"
      aria-label="Install Landoo"
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-brown-dark">Install Landoo</div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            {iosHint && !event ? (
              <>
                Tap <Share className="inline h-3 w-3" /> Share, then “Add to Home Screen”.
              </>
            ) : (
              "Add it to your home screen for a full-screen, app-like experience."
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {event && (
            <button
              type="button"
              onClick={install}
              className="inline-flex items-center gap-1.5 rounded-full bg-brown px-3.5 py-2 text-xs font-semibold text-primary-foreground"
            >
              <Download className="h-3.5 w-3.5" /> Install
            </button>
          )}
          <button
            type="button"
            onClick={close}
            aria-label="Dismiss install banner"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary/70 text-brown-dark"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
